import React, { memo, useRef } from 'react';
import { Table, Button, message } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { connect } from 'react-redux';
import intl from 'react-intl-universal';

import { format2FullTimeStr } from '@/utils/date';
import { PERM_CODES } from '@/config/permCode';
import FixStickyTable from '@/components/FixStickyTable';
import {
  OperationLogData,
  OperationPagination,
  OperationTagMap,
  OperationResultMap,
} from '@/pages/order/operationLog/helper';

import DetailModal, { DetailModalRef } from '../DetailModal';

import styles from './index.module.scss';

interface TableProps {
  dataSource: OperationLogData[];
  pagination?: OperationPagination;
  getTableData: (params) => void;
}

const mapStateToProps = (store) => {
  return { permissions: store.user.permissions || [] };
};
type MapStateProps = ReturnType<typeof mapStateToProps>;

const SpecialCodeNameMap = {
  yPMS: 'PMS',
};

const Index: React.FC<TableProps & MapStateProps> = (props) => {
  const detailModalRef = useRef<DetailModalRef>();

  const { permissions, dataSource, pagination, getTableData } = props;

  const handlePaginationChange = (param) => {
    getTableData({ pageNum: param.current });
  };

  const showDetailModal = (record: OperationLogData) => {
    if (permissions.includes(PERM_CODES.ORDER_OPERATION_LOG_DETAIL)) {
      detailModalRef.current.showModal(record.id);
    } else {
      //
      message.error(intl.get('app_permission_denied'));
    }
  };

  // 操作人
  const renderOperator = (record: OperationLogData) => {
    return [record.operator, record.operatorName].filter(Boolean).join(' ');
  };

  // 业务系统
  const renderSystemName = (val) => SpecialCodeNameMap[val] || val;

  // 操作内容
  const renderContent = (val) => {
    return <div className={styles.logContent}>{val}</div>;
  };

  // 日志标签
  const renderTagList = (list) => {
    if (list && list.length) {
      return list.map((item) => OperationTagMap[item]).join('、');
    }
    return null;
  };

  // 操作结果
  const renderResult = (val) => {
    return OperationResultMap[val];
  };

  // 表格操作
  const renderAction = (record: OperationLogData) => {
    return (
      <Button type="link" onClick={() => showDetailModal(record)}>
        {intl.get('order_operation_log_detail')}
      </Button>
    );
  };

  // 表格列
  const tableColumn: ColumnProps<OperationLogData>[] = [
    {
      title: intl.get('roomstate_take_care_record_column_crate_time'),
      dataIndex: 'bizTime',
      render: format2FullTimeStr,
    },
    { title: intl.get('reports_cross_refund_col_tit12'), key: 'operator', render: renderOperator },
    { title: intl.get('drawer_checkin_guest_reserve_order_no'), dataIndex: 'orderNo' },
    {
      title: intl.get('manage_employee_process_column_system'),
      dataIndex: 'systemName',
      render: renderSystemName,
    },
    { title: intl.get('order_operation_log_page_name'), dataIndex: 'operationPage' },
    {
      title: intl.get('order_operation_log_before'),
      dataIndex: 'beforeContent',
      render: renderContent,
    },
    {
      title: intl.get('order_operation_log_after'),
      dataIndex: 'afterContent',
      render: renderContent,
    },
    { title: intl.get('order_operation_log_tag'), dataIndex: 'tags', render: renderTagList },
    {
      title: intl.get('order_operation_log_result'),
      dataIndex: 'operationResult',
      render: renderResult,
    },
    {
      title: intl.get('app_table_column_action'),
      fixed: 'right',
      align: 'right',
      render: renderAction,
    },
  ];

  return (
    <div className={styles.tableWrapper}>
      <FixStickyTable>
        <Table
          rowKey="id"
          columns={tableColumn}
          scroll={{ x: 'max-content' }}
          pagination={
            pagination
              ? {
                  ...pagination,
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total) =>
                    intl.get('app_table_pagination_total', { total, size: pagination.pageSize }),
                }
              : false
          }
          sticky
          dataSource={dataSource}
          onChange={handlePaginationChange}
        />
      </FixStickyTable>
      <DetailModal innerRef={detailModalRef} />
    </div>
  );
};

export default connect<MapStateProps>(mapStateToProps)(memo(Index));
